/**
 * @file FontCatalogDialog.tsx
 * @description Окно выбора шрифта роли (phase19.16 Р1, Р1-7; SB10, SB99;
 * uiux-design-r1 §1 и проект своих шрифтов §1–§5): оболочка окна, само
 * содержимое — `FontCatalogBody` (каталог и второй экран «Свой шрифт»).
 *
 * Содержимое монтируется заново при каждом открытии (окно размонтирует его
 * при закрытии), поэтому экран, выбор и поиск начинаются с чистого листа без
 * сброса в эффекте. Escape на втором экране с выбранными файлами возвращает к
 * каталогу, а не закрывает окно (`escapeGuard` от содержимого). На 1280 —
 * окно 720px, на телефоне — весь экран.
 */

import type { SiteTheme } from "@chaiberi/shared";
import { useRef, type ReactNode } from "react";

import { Dialog, DialogContent } from "../../ui/dialog";
import { FontCatalogBody } from "./FontCatalogBody";
import type { CategoryFilter } from "./FontCatalogList";

export { FONT_SAMPLE_TEXT } from "./FontCatalogList";

interface Props {
  open: boolean;
  title: string;
  current: string | null;
  /** Фильтр при открытии: для рукописной роли — «Рукописные». */
  initialCategory?: CategoryFilter;
  /** Шрифты ролей в текущей (локальной) теме — для предупреждения при удалении. */
  draftFonts?: SiteTheme["fonts"] | null;
  onClose: () => void;
  onSelect: (key: string) => void;
  /** Перейти к разделу «Дизайны» (из окна «Шрифт используется»). */
  onOpenDesigns?: () => void;
  /** Быстрый выбор в начале списка — см. `FontCatalogBody` (окно шрифта элемента, SB120). */
  leading?: (chosen: string | null, choose: (key: string) => void) => ReactNode;
}

export function FontCatalogDialog({ open, title, current, initialCategory = "all", draftFonts = null, onClose, onSelect, onOpenDesigns, leading }: Props) {
  // Содержимое говорит, перехватить ли Escape (второй экран с файлами → назад).
  const escapeGuard = useRef<(() => boolean) | null>(null);

  return (
    <Dialog
      open={open}
      onOpenChange={(next, details) => {
        if (next) return;
        if (details.reason === "escape-key" && escapeGuard.current?.()) {
          details.cancel();
          return;
        }
        onClose();
      }}
    >
      <DialogContent fullScreen className="flex flex-col max-md:gap-3 sm:max-h-[90vh] sm:max-w-[45rem]">
        <FontCatalogBody
          title={title}
          current={current}
          initialCategory={initialCategory}
          draftFonts={draftFonts}
          escapeGuard={escapeGuard}
          onClose={onClose}
          onSelect={onSelect}
          onOpenDesigns={onOpenDesigns}
          leading={leading}
        />
      </DialogContent>
    </Dialog>
  );
}
